import { DeletionWorkflowFactory } from './DeletionWorkflowOrchestrator';
import SyncBoxConfig from './SyncBoxConfig';
import SyncBoxState from './SyncBoxState';
export class DataPruningService {
    /**
     * Check for stale syncBoxes and mark them for deletion
     */
    async checkForStaleSyncBoxes() {
        const staleSyncBoxes = [];
        const now = Date.now();
        try {
            // Get all syncBox configs
            const allConfigs = await SyncBoxConfig.getAllConfigs();
            for (const config of allConfigs) {
                // Skip if already marked for deletion
                if (config.markedForDeletion) {
                    continue;
                }
                // Get the syncBox state to check lastAccessedAt
                const syncBoxState = new SyncBoxState(config.id);
                const lastAccessedAt = await syncBoxState.getLastAccessedAt();
                // Skip if staleThresholdDays is 0 or negative
                if (config.staleThresholdDays <= 0) {
                    continue;
                }
                const staleThresholdMs = config.staleThresholdDays * 24 * 60 * 60 * 1000;
                const isStale = (now - lastAccessedAt) > staleThresholdMs;
                if (isStale) {
                    staleSyncBoxes.push(config);
                }
            }
        }
        catch (error) {
            console.error('Error checking for stale syncBoxes:', error);
        }
        return staleSyncBoxes;
    }
    /**
     * Mark syncBoxes for deletion
     */
    async markSyncBoxesForDeletion(syncBoxes) {
        try {
            for (const syncBox of syncBoxes) {
                await SyncBoxConfig.markForDeletion(syncBox.id);
            }
        }
        catch (error) {
            console.error('Error marking syncBoxes for deletion:', error);
        }
    }
    /**
     * Execute deletion workflow for syncBoxes marked for deletion
     */
    async executeDeletionWorkflows() {
        try {
            const syncBoxesToDelete = await SyncBoxConfig.getConfigsMarkedForDeletion();
            for (const syncBoxConfig of syncBoxesToDelete) {
                await this.executeDeletionWorkflow(syncBoxConfig);
            }
        }
        catch (error) {
            console.error('Error executing deletion workflows:', error);
        }
    }
    /**
     * Execute deletion workflow for a single syncBox
     */
    async executeDeletionWorkflow(syncBoxConfig) {
        try {
            const workflow = DeletionWorkflowFactory.getWorkflow(syncBoxConfig.collectionName);
            if (workflow) {
                // Execute the workflow
                await workflow.execute(syncBoxConfig);
            }
            // Delete the syncBox config and state after workflow execution
            await this.deleteSyncBoxConfigAndState(syncBoxConfig.id);
        }
        catch (error) {
            console.error(`Error executing deletion workflow for syncBox ${syncBoxConfig.id}:`, error);
        }
    }
    /**
     * Delete syncBox config and state
     */
    async deleteSyncBoxConfigAndState(syncBoxId) {
        try {
            // Delete syncBox state
            await SyncBoxState.delete(syncBoxId);
            // Delete syncBox config
            await SyncBoxConfig.delete(syncBoxId);
        }
        catch (error) {
            console.error(`Error deleting syncBox config and state for ${syncBoxId}:`, error);
        }
    }
}
export const dataPruningService = new DataPruningService();
/**
 * Execute the data pruning flow
 */
export async function initDataPruningService() {
    try {
        // Check for stale syncBoxes and mark them for deletion
        const staleSyncBoxes = await dataPruningService.checkForStaleSyncBoxes();
        await dataPruningService.markSyncBoxesForDeletion(staleSyncBoxes);
        // Execute deletion workflows for syncBoxes marked for deletion
        await dataPruningService.executeDeletionWorkflows();
    }
    catch (error) {
        console.error('Error executing data pruning flow:', error);
    }
}
